import { CellContext, Header, SortingFn } from '@tanstack/react-table';
import { Measure } from 'services/model/model.service';
import { MeasureFormat } from 'components/measure-unit/MeasureFormat';
import { MeasureComparison } from 'components/MeasureComparison';
import { IntlShape } from 'react-intl';
import { addImageNotAvailable, buildApiImageUrl } from 'services/product-data.service';
import { TenantConfig } from 'context/TenantConfigContext';
import { ReactElement } from 'react';
import { EllipsedTextWithTooltip } from 'components/EllipsedTextWithTooltip';
import { Tooltip } from 'components/Tooltip';
import { cn } from 'utils/classNamesTailwind';
import {
  ComparisonCell,
  GenericMeasureCellContext,
  getMeasureEntryValue,
} from 'components/table/useDataEntriesInTables';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sortComparisonCell: SortingFn<any> = (rowA, rowB, columnId) => {
  const valueA: ComparisonCell = rowA.getValue(columnId);
  const valueB: ComparisonCell = rowB.getValue(columnId);

  const sortByDisplayValue = valueA.displayValue && valueB.displayValue;
  const a = sortByDisplayValue ? valueA.displayValue! : valueA.value;
  const b = sortByDisplayValue ? valueB.displayValue! : valueB.value;

  return a === b ? 0 : a > b ? 1 : -1;
};

export const createMeasureCell = (
  context: GenericMeasureCellContext,
  measure: Measure,
  signedValue = false,
  comparisonActive = true
) => {
  const measureEntry = context.getValue();
  if (measureEntry === undefined) {
    // Not a comparison but just a single value; display as right aligned number given the metric's settings
    return (
      <div className="flex flex-col xl:flex-row xl:items-center items-end justify-end">
        <span>Missing measure: {measure.code}</span>
      </div>
    );
  }
  if (typeof measureEntry === 'number') {
    // Not a comparison but just a single value; display as right aligned number given the metric's settings
    return (
      <div className="flex flex-col xl:flex-row xl:items-center items-end justify-end">
        <MeasureFormat value={measureEntry} unit={measure.metric.unit} config={{ hasSign: signedValue }} />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col xl:flex-row xl:items-center justify-between',
        comparisonActive ? 'justify-between' : 'justify-end'
      )}
    >
      <MeasureFormat
        value={getMeasureEntryValue(measureEntry)}
        unit={measure.metric.unit}
        config={{ hasSign: signedValue }}
      />
      {comparisonActive && (
        <MeasureComparison
          value={measureEntry.value}
          comparisonValue={measureEntry.compareValue}
          unit={measure.metric.unit}
          interpretation={measure.metric.interpretation}
        />
      )}
    </div>
  );
};

export const createExpanderCell = <T,>(
  context: CellContext<T, string>,
  cellValue: ReactElement,
  showChildElements = false,
  tooltipClassName = ''
) => {
  return context.row.getCanExpand() ? (
    <div className="flex items-center h-[48px]">
      <Tooltip
        tooltipContent={cellValue}
        className={cn('flex-1 min-w-0 break-words whitespace-normal line-clamp-2', tooltipClassName)}
      >
        {cellValue}
      </Tooltip>
    </div>
  ) : showChildElements ? (
    <div className="flex items-center">
      <Tooltip
        tooltipContent={cellValue}
        className={cn('flex-1 min-w-0 break-words whitespace-normal line-clamp-2', tooltipClassName)}
      >
        {cellValue}
      </Tooltip>
    </div>
  ) : (
    ''
  );
};

export const createArticleImageCellFromUrlAttribute = (imageUrl: string, tenantContext: TenantConfig) => {
  return (
    <div className="flex items-center justify-center ">
      <img
        src={buildApiImageUrl(tenantContext.accountNumber, imageUrl)}
        onError={addImageNotAvailable}
        alt="Product Thumbnail"
        className="w-12"
      />
    </div>
  );
};

export const createArticleImageCell = <T,>(context: CellContext<T, string>, tenantContext: TenantConfig) => {
  return createArticleImageCellFromUrlAttribute(context.getValue(), tenantContext);
};

export const createValueCountCell = (cellValue: string, deliminator = ',') => {
  const split = cellValue.split(deliminator);

  const displayValue = split.length === 1 ? cellValue : `(${split.length}) ${cellValue}`;

  return <span className="max-w-[150px] break-words line-clamp-3">{displayValue}</span>;
};

export const getSubtitleFromHeader = <T,>(header: Header<T, unknown>, intl: IntlShape) => {
  const headerValue = header.column.columnDef.header;
  if (headerValue) {
    let columnHeader;
    if (typeof headerValue === 'function') {
      columnHeader = headerValue(header.getContext()) as string;
    } else {
      columnHeader = headerValue;
    }

    if (columnHeader) {
      return intl.formatMessage({ id: 'general.title.by-metric' }, { metric: columnHeader });
    }

    return '';
  }

  return '';
};

export const createSubtitledHeaderCell = (
  title: string,
  subtitle: string,
  titleClass?: string,
  subtitleClass?: string
) => {
  return (
    <div className="flex flex-col ">
      <EllipsedTextWithTooltip className={cn('line-clamp-2', titleClass)} text={title} />
      <EllipsedTextWithTooltip
        className={cn('text-xs text-gray-400 font-normal line-clamp-2', subtitleClass)}
        text={subtitle}
      />
    </div>
  );
};

export const createNumberCell = (formattedNumber: string, className?: string) => {
  return <div className={cn(`text-right`, className)}>{formattedNumber}</div>;
};

export const formatValueAsPositiveOrNegative = (value: number, invert = false) => {
  const compareValue = invert ? value * -1 : value;

  if (compareValue > 0) {
    return 'text-laurel-600 font-semibold';
  } else if (compareValue < 0) {
    return 'text-coral-500 font-semibold';
  }
  return '';
};
